import { groupBy } from 'lodash';
import { CircleAlert, CirclePlus, ClipboardPen, FolderOpen, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useCallback, forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditProgram from '../EditProgram';
import GroupsModal from './GroupsModal';
import type {
    DeleteAssessmentRunsMutation,
    DeleteAssessmentRunsMutationVariables,
    DeleteProgramMutation,
    DeleteProgramMutationVariables,
    ProgramListItemDataFragment,
} from './ProgramListing.api';
import { DeleteAssessmentRunsDocument, DeleteProgramDocument, useFetchProgramsQuery } from './ProgramListing.api';
import useProgramListReducer from './useProgramListReducer';
import type { ProgramListingItem, ProgramListingItemGroup } from './utils';
import { ProgramColumnKey, getSortField, ProgramSubColumnKey } from './utils';
import client from '@/apolloClient';
import { ProgramSortingField as DefaultSortField, SortingOrder as GqlSortingOrder } from '@/apolloClient.types';
import useAppAbilities from '@/components/abilities';
import Empty from '@/components/blocks/Empty';
import Badge from '@/components/common/Badge';
import EmptyContainer from '@/components/common/EmptyContainer';
import IconButton from '@/components/common/IconButton';
import Loader from '@/components/common/Loader';
import Table from '@/components/common/Table';
import {
    SortingOrder,
    type TableProps,
    type SortOrder as TableSortOrderValue,
    type ExpandableConfig,
} from '@/components/common/Table/types';
import TableFooter from '@/components/common/TableFooter';
import { useUser } from '@/contexts/UserSession';
import { confirm } from '@/utils/ConfirmModal';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import useDateFormats from '@/utils/useDateFormats';

interface ProgramListingProps {
    searchText?: string;
    companyId: string;
    onCreateAssessmentRuns: (record: ProgramListItemDataFragment) => void;
    onUpdateAssessmentRuns: (program: ProgramListItemDataFragment, group: ProgramListingItemGroup) => void;
}

export interface ProgramListingRef {
    refetch: () => void;
}

const pageSizeOptions = [10, 25, 50, 100];

const transformProgramsToListingItems = (programs: ProgramListItemDataFragment[]): ProgramListingItem[] => {
    return programs.map(program => {
        const groupsByGroupId = groupBy(program.assessmentRuns, 'groupId');
        return {
            ...program,
            groups: Object.entries(groupsByGroupId).map(([groupId, runs]) => {
                const sortedRuns = runs.sort(
                    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
                );
                const firstRun = sortedRuns[0];
                const lastRun = sortedRuns[sortedRuns.length - 1];
                return {
                    programId: program.id,
                    groupId,
                    name: runs.find(run => run.group?.name)?.group?.name || '',
                    assessmentRuns: runs,
                    startDate: firstRun?.startDate,
                    endDate: lastRun?.endDate,
                };
            }),
        };
    });
};

const ProgramListing = forwardRef<ProgramListingRef, ProgramListingProps>(
    ({ searchText, companyId, onCreateAssessmentRuns, onUpdateAssessmentRuns }, ref) => {
        const { t } = useTranslation('programManagementPage');
        const user = useUser(true);
        const abilities = useAppAbilities();
        const { formatDate } = useDateFormats();

        const [state, dispatch] = useProgramListReducer();
        const { page, pageSize, sort, filter } = state;

        // Modal states
        const [groupsModalOpen, setGroupsModalOpen] = useState(false);
        const [selectedGroup, setSelectedGroup] = useState<ProgramListingItemGroup | null>(null);
        const [selectedProgram, setSelectedProgram] = useState<ProgramListItemDataFragment | undefined>(undefined);
        const [editProgramModalOpen, setEditProgramModalOpen] = useState(false);

        // Fetch program instances for the selected/current company
        const { loading, data, error, refetch } = useFetchProgramsQuery({
            fetchPolicy: 'cache-and-network',
            variables: {
                companyId: companyId || '',
                pagination: {
                    limit: pageSize,
                    offset: (page - 1) * pageSize,
                },
                sort: { field: sort.field, order: sort.order },
                filter: filter,
            },
            skip: !companyId,
        });

        useImperativeHandle(
            ref,
            () => ({
                refetch,
            }),
            [refetch]
        );

        useEffect(() => {
            dispatch({
                type: 'setFilter',
                filter: {
                    searchText: searchText?.trim() || undefined,
                },
            });
        }, [searchText, dispatch]);

        const handleSortChange = useCallback(
            (columnKey?: string, order?: TableSortOrderValue) => {
                if (!columnKey || !order) {
                    dispatch({
                        type: 'setSort',
                        sortBy: {
                            field: DefaultSortField.Name,
                            order: GqlSortingOrder.Asc,
                            orderValue: SortingOrder.Asc,
                        },
                    });
                    return;
                }

                const programColumnKey = columnKey as ProgramColumnKey;
                const field = getSortField(programColumnKey);

                if (field) {
                    dispatch({
                        type: 'setSort',
                        sortBy: {
                            field: field,
                            order: order === SortingOrder.Asc ? GqlSortingOrder.Asc : GqlSortingOrder.Desc,
                            orderValue: order,
                        },
                    });
                } else {
                    dispatch({
                        type: 'setSort',
                        sortBy: {
                            field: DefaultSortField.Name,
                            order: GqlSortingOrder.Asc,
                            orderValue: SortingOrder.Asc,
                        },
                    });
                }
            },
            [dispatch]
        );

        const handleEditProgram = useCallback((record: ProgramListItemDataFragment) => {
            setEditProgramModalOpen(true);
            setSelectedProgram(record);
        }, []);

        const closeEditProgramModal = useCallback(() => {
            setEditProgramModalOpen(false);
            setSelectedProgram(undefined);
        }, []);

        const handleDeleteProgram = useCallback(
            async (record: ProgramListItemDataFragment) => {
                if (!companyId) {
                    return;
                }

                confirm(
                    {
                        title: t('programList.confirmDelete.title', { name: record.name }),
                        content: t('programList.confirmDelete.message'),
                        isDeletion: true,
                    },
                    async () => {
                        const variables: DeleteProgramMutationVariables = {
                            id: record.id,
                            companyId: companyId,
                        };

                        const { data: mutationData } = await client.mutate<DeleteProgramMutation>({
                            mutation: DeleteProgramDocument,
                            variables,
                        });

                        if (mutationData?.deleteProgram?.__typename === 'DeleteProgramSuccessfulResponse') {
                            refetch();
                            showSuccessNotification({
                                message: t('programList.deleteSuccess.title'),
                                description: t('programList.deleteSuccess.message', { name: record.name }),
                            });
                            return;
                        }

                        if (mutationData?.deleteProgram?.__typename === 'ErrorResponse') {
                            const errorFields = mutationData.deleteProgram.fields;
                            if (errorFields && errorFields.length > 0) {
                                showErrorNotification({
                                    message: t('programList.deleteError.title'),
                                    description: errorFields[0]?.message || t('programList.deleteError.message'),
                                });
                                return;
                            }
                        }

                        showErrorNotification({
                            message: t('programList.deleteError.title'),
                            description: t('programList.deleteError.message'),
                        });
                    }
                );
            },
            [t, refetch, companyId]
        );

        const handleViewAssessmentRuns = useCallback((record: ProgramListingItemGroup) => {
            setSelectedGroup(record);
            setGroupsModalOpen(true);
        }, []);

        const handleCreateAssessmentRun = useCallback(
            (record: ProgramListItemDataFragment) => {
                onCreateAssessmentRuns(record);
            },
            [onCreateAssessmentRuns]
        );

        const handleEditAssessmentRuns = useCallback(
            (record: ProgramListingItemGroup) => {
                const program = data?.programs.items.find(p => p.id === record.programId);
                if (program) {
                    onUpdateAssessmentRuns(program, record);
                }
            },
            [data?.programs.items, onUpdateAssessmentRuns]
        );

        const handleDeleteAssessmentRuns = useCallback(
            (record: ProgramListingItemGroup) => {
                if (!companyId && !record.programId) {
                    return;
                }

                confirm(
                    {
                        title: t('assessmentRuns.confirmDelete.title', { name: record.name }),
                        content: t('assessmentRuns.confirmDelete.message'),
                        isDeletion: true,
                    },
                    async () => {
                        const variables: DeleteAssessmentRunsMutationVariables = {
                            programId: record.programId,
                            groupId: record.groupId,
                            companyId: companyId,
                        };

                        const { data: mutationData } = await client.mutate<DeleteAssessmentRunsMutation>({
                            mutation: DeleteAssessmentRunsDocument,
                            variables,
                        });

                        if (
                            mutationData?.deleteAssessmentRuns?.__typename === 'DeleteAssessmentRunsSuccessfulResponse'
                        ) {
                            refetch();
                            showSuccessNotification({
                                message: t('assessmentRuns.deleteSuccess.title'),
                                description: t('assessmentRuns.deleteSuccess.message', { name: record.name }),
                            });
                            return;
                        }

                        if (mutationData?.deleteAssessmentRuns?.__typename === 'ErrorResponse') {
                            const errorFields = mutationData.deleteAssessmentRuns.fields;
                            if (errorFields && errorFields.length > 0) {
                                showErrorNotification({
                                    message: t('assessmentRuns.deleteError.title'),
                                    description: errorFields[0]?.message || t('assessmentRuns.deleteError.message'),
                                });
                                return;
                            }
                        }

                        showErrorNotification({
                            message: t('assessmentRuns.deleteError.title'),
                            description: t('assessmentRuns.deleteError.message'),
                        });
                    }
                );
            },
            [t, refetch, companyId]
        );

        const handleCloseGroupsModal = useCallback(() => {
            setGroupsModalOpen(false);
            setSelectedGroup(null);
        }, []);

        const handlePageChange = useCallback(
            (newPage: number) => {
                dispatch({ type: 'setPage', page: newPage });
            },
            [dispatch]
        );

        const handlePageSizeChange = useCallback(
            (newPageSize: number) => {
                dispatch({ type: 'setPageSize', pageSize: newPageSize });
            },
            [dispatch]
        );

        const columns = useMemo((): TableProps['columns'] => {
            const hasActionsColumn =
                abilities.canCreateAssessmentRuns() || abilities.canEditProgram() || abilities.canDeleteProgram();

            const baseColumns: TableProps['columns'] = [
                {
                    title: t('programList.tableHeaders.name'),
                    dataIndex: 'name',
                    key: ProgramColumnKey.ProgramName,
                    slotKey: 'programName',
                    className: hasActionsColumn ? 'w-4/12' : 'w-5/12',
                    render: name => (
                        <span className="block truncate font-medium" title={name}>
                            {name}
                        </span>
                    ),
                    sorter: true,
                    sortOrder: sort.field === DefaultSortField.Name ? sort.orderValue : undefined,
                },
                {
                    title: t('programList.tableHeaders.courseName'),
                    dataIndex: ['courseInstance', 'name'],
                    key: ProgramColumnKey.CourseName,
                    slotKey: 'courseName',
                    className: hasActionsColumn ? 'w-3/12' : 'w-4/12',
                    render: courseName => (
                        <div className="max-w-[200px]">
                            <span className="block truncate" title={courseName}>
                                {courseName || '-'}
                            </span>
                        </div>
                    ),
                    sorter: true,
                    sortOrder: sort.field === DefaultSortField.CourseInstanceName ? sort.orderValue : undefined,
                },
                {
                    title: t('programList.tableHeaders.createdAt'),
                    dataIndex: 'createdAt',
                    key: ProgramColumnKey.CreatedAt,
                    slotKey: 'createdAt',
                    className: hasActionsColumn ? 'w-2/12' : 'w-3/12',
                    render: createdAt => formatDate(createdAt),
                    sorter: true,
                    sortOrder: sort.field === DefaultSortField.CreatedAt ? sort.orderValue : undefined,
                },
            ];

            if (hasActionsColumn) {
                baseColumns.push({
                    title: t('programList.tableHeaders.actions'),
                    dataIndex: 'id',
                    key: ProgramColumnKey.Actions,
                    slotKey: 'actions',
                    className: 'w-3/12 text-right',
                    render: (_, record) => (
                        <div className="flex justify-end space-x-1">
                            {abilities.canCreateAssessmentRuns() && (
                                <IconButton
                                    onClick={() => handleCreateAssessmentRun(record)}
                                    icon={<CirclePlus className="size-4 text-green-500" strokeWidth={2} />}
                                    title={t('programList.actions.create')}
                                />
                            )}
                            {abilities.canEditProgram() && (
                                <IconButton
                                    icon={<Pencil className="size-4 text-yellow-500" strokeWidth={2} />}
                                    onClick={() => handleEditProgram(record)}
                                    title={t('programList.actions.edit')}
                                />
                            )}
                            {abilities.canDeleteProgram() && (
                                <IconButton
                                    icon={<Trash2 className="size-4 text-red-500" strokeWidth={2} />}
                                    onClick={() => handleDeleteProgram(record)}
                                    title={t('programList.actions.delete')}
                                />
                            )}
                        </div>
                    ),
                });
            }

            return baseColumns;
        }, [
            t,
            sort.field,
            sort.orderValue,
            abilities,
            formatDate,
            handleCreateAssessmentRun,
            handleEditProgram,
            handleDeleteProgram,
        ]);

        const subColumns = useMemo((): TableProps['columns'] => {
            const hasSubActionsColumn = abilities.canEditAssessmentRuns() || abilities.canDeleteAssessmentRuns();

            const baseSubColumns: TableProps['columns'] = [
                {
                    title: t('programList.tableHeaders.groupName'),
                    dataIndex: 'name',
                    slotKey: 'groupName',
                    className: 'w-3/12',
                    key: ProgramSubColumnKey.GroupName,
                },
                {
                    title: t('programList.tableHeaders.assessmentRuns'),
                    dataIndex: 'assessmentRuns',
                    slotKey: 'assessmentRuns',
                    className: hasSubActionsColumn ? 'w-2/12' : 'w-3/12',
                    key: ProgramSubColumnKey.AssessmentRuns,
                    render: (assessmentRuns, record) => {
                        return (
                            <button
                                onClick={() => abilities.canViewAssessmentRuns() && handleViewAssessmentRuns(record)}
                                className="cursor-pointer"
                                title={t('programList.actions.view')}
                            >
                                <Badge
                                    icon={<ClipboardPen className="size-4" />}
                                    color={assessmentRuns.length > 0 ? 'teal' : 'gray'}
                                    text={assessmentRuns.length}
                                    style={'shadow'}
                                />
                            </button>
                        );
                    },
                },
                {
                    title: t('programList.tableHeaders.startDate'),
                    dataIndex: 'startDate',
                    slotKey: 'startDate',
                    className: hasSubActionsColumn ? 'w-2/12' : 'w-3/12',
                    key: ProgramSubColumnKey.StartDate,
                    render: startDate => formatDate(startDate),
                },
                {
                    title: t('programList.tableHeaders.endDate'),
                    dataIndex: 'endDate',
                    slotKey: 'endDate',
                    className: hasSubActionsColumn ? 'w-2/12' : 'w-3/12',
                    key: ProgramSubColumnKey.EndDate,
                    render: endDate => <span className="w-full">{formatDate(endDate)}</span>,
                },
            ];

            if (hasSubActionsColumn) {
                baseSubColumns.push({
                    dataIndex: 'id',
                    slotKey: 'actions',
                    className: 'w-3/12 text-right',
                    key: ProgramColumnKey.Actions,
                    render: (_, record) => (
                        <div className="flex justify-end space-x-1">
                            {abilities.canEditAssessmentRuns() && (
                                <IconButton
                                    icon={<Pencil className="size-4 text-yellow-500" strokeWidth={2} />}
                                    onClick={() => handleEditAssessmentRuns(record)}
                                    title={t('programList.actions.edit')}
                                />
                            )}
                            {abilities.canDeleteAssessmentRuns() && (
                                <IconButton
                                    icon={<Trash2 className="size-4 text-red-500" strokeWidth={2} />}
                                    onClick={() => handleDeleteAssessmentRuns(record)}
                                    title={t('programList.actions.delete')}
                                />
                            )}
                        </div>
                    ),
                });
            }

            return baseSubColumns;
        }, [t, abilities, formatDate, handleViewAssessmentRuns, handleEditAssessmentRuns, handleDeleteAssessmentRuns]);

        const expandable: ExpandableConfig<ProgramListingItemGroup> = useMemo(() => {
            const parentHasActionsColumn =
                abilities.canCreateAssessmentRuns() || abilities.canEditProgram() || abilities.canDeleteProgram();
            const childHasActionsColumn = abilities.canEditAssessmentRuns() || abilities.canDeleteAssessmentRuns();

            return {
                childrenField: 'groups',
                childColumns: subColumns,
                showChildHeaders: true,
                // Map child columns into parent slots to ensure consistent alignment and width sharing
                childColumnGroups: {
                    programName: ['groupName', 'assessmentRuns'],
                    courseName: ['startDate'],
                    createdAt: ['endDate'],
                    ...(parentHasActionsColumn && childHasActionsColumn ? { actions: ['actions'] } : {}),
                },
            };
        }, [abilities, subColumns]);

        const totalCount = data?.programs.count || 0;

        const dataSource = useMemo(() => {
            return data?.programs.items ? transformProgramsToListingItems(data.programs.items) : [];
        }, [data?.programs.items]);

        // Handle program instances error
        if (error) {
            console.error('Error loading program instances:', error);
            return (
                <div className="flex h-[50vh] items-center justify-center">
                    <EmptyContainer>
                        <Empty
                            title={t('programList.empty.errorTitle')}
                            description={t('programList.empty.errorDesc')}
                            icon={CircleAlert}
                        />
                    </EmptyContainer>
                </div>
            );
        }

        // Don't render anything if no company is selected for superadmin
        if (user.isSuperAdmin && !companyId) {
            return (
                <div className="flex h-[50vh] items-center justify-center">
                    <EmptyContainer>
                        <Empty title={t('programList.empty.selectCompany')} />
                    </EmptyContainer>
                </div>
            );
        }

        if (loading && !data?.programs.items.length) {
            return <Loader />;
        }

        if (!data?.programs.items.length && !loading) {
            return (
                <div className="flex h-[50vh] items-center justify-center">
                    <EmptyContainer>
                        <Empty
                            title={t('programList.empty.noPrograms')}
                            description={t('programList.empty.noProgramsDescription')}
                            icon={FolderOpen}
                        />
                    </EmptyContainer>
                </div>
            );
        }

        return (
            <>
                <div className="flex flex-col gap-5">
                    <Table
                        columns={columns}
                        expandable={expandable}
                        dataSource={dataSource}
                        loading={loading}
                        onSortChange={handleSortChange}
                        rowKey="id"
                    />

                    {totalCount > 0 && (
                        <TableFooter
                            currentPage={page}
                            pageSize={pageSize}
                            totalCount={totalCount}
                            pageSizeOptions={pageSizeOptions}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </div>

                <GroupsModal open={groupsModalOpen} onClose={handleCloseGroupsModal} group={selectedGroup} />

                <EditProgram
                    program={selectedProgram}
                    companyId={companyId}
                    onCreated={() => {
                        setEditProgramModalOpen(false);
                        refetch();
                    }}
                    open={editProgramModalOpen}
                    onClose={closeEditProgramModal}
                />
            </>
        );
    }
);

ProgramListing.displayName = 'ProgramListing';

export default ProgramListing;
